/* Situație pe agenții — carduri Încărcate (data vânzării) vs CSD (data CSD),
 * cu defalcare pe categorii de produs. Selector Lună curentă / Trimestru curent.
 * Vizibil doar admin / manager / backoffice. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Building2, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const CHANNEL_LABELS = {
  d2d: { label: "D2D", cls: "bg-indigo-100 text-indigo-700" },
  telesales: { label: "Telesales", cls: "bg-cyan-100 text-cyan-700" },
  iasi: { label: "Shop Iași", cls: "bg-amber-100 text-amber-700" },
  bacau: { label: "Shop Bacău", cls: "bg-amber-100 text-amber-700" },
  cluj: { label: "Shop Cluj", cls: "bg-amber-100 text-amber-700" },
  oradea: { label: "Shop Oradea", cls: "bg-amber-100 text-amber-700" },
};

function periodParams(mode) {
  const now = new Date();
  const y = now.getFullYear();
  if (mode === "month") {
    const m = now.getMonth() + 1;
    return { year: y, mstart: m, mend: m };
  }
  const q = Math.floor(now.getMonth() / 3);
  return { year: y, mstart: 3 * q + 1, mend: 3 * q + 3 };
}

const MONTH_NAMES = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"];

export default function AgencyProgressCards() {
  const [mode, setMode] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/dashboard/agency-progress`, { params: periodParams(mode) });
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const p = periodParams(mode);
  const periodLabel = mode === "month"
    ? `${MONTH_NAMES[p.mstart - 1]} ${p.year}`
    : `T${Math.floor((p.mstart - 1) / 3) + 1} ${p.year} (${MONTH_NAMES[p.mstart - 1]}–${MONTH_NAMES[p.mend - 1]})`;

  return (
    <Card className="border-l-4 border-l-teal-600 shadow-sm" data-testid="agency-progress-section">
      <CardHeader className="pb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-700" />
              Situație pe agenții
            </CardTitle>
            <CardDescription>
              Contracte <span className="font-medium text-slate-700">Încărcate</span> (data vânzării) vs <span className="font-medium text-emerald-700">CSD</span> (data CSD) · {periodLabel} · HVAS în puncte × cantitate · regulă PPC: EE/GAZ includ anulate după CSD, fără Transfer/Product Change
            </CardDescription>
          </div>
          <div className="inline-flex rounded-full border border-slate-200 p-0.5 self-start" data-testid="agency-progress-period-toggle">
            <button
              type="button"
              onClick={() => setMode("month")}
              className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${mode === "month" ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="agency-progress-month"
            >
              Luna curentă
            </button>
            <button
              type="button"
              onClick={() => setMode("quarter")}
              className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${mode === "quarter" ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="agency-progress-quarter"
            >
              Trimestrul curent
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44 rounded-lg" />)}
          </div>
        ) : !data || data.agencies.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">Nu există agenții active.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.agencies.map((a) => {
              const ch = CHANNEL_LABELS[a.channel];
              return (
                <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow" data-testid={`agency-progress-card-${a.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <p className="font-semibold text-slate-900 truncate" title={a.name}>{a.name}</p>
                    {ch && <Badge className={`${ch.cls} border-0 text-[10px] shrink-0`}>{ch.label}</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="rounded-md bg-slate-50 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Încărcate</p>
                      <p className="text-2xl font-bold text-[#1e3a8a]" data-testid={`agency-uploaded-${a.id}`}>{a.uploaded_total}</p>
                      {a.uploaded_cancelled > 0 && (
                        <p className="text-[10px] text-rose-500" title="Anulate după CSD — incluse conform regulii PPC">din care {a.uploaded_cancelled} anulate</p>
                      )}
                    </div>
                    <div className="rounded-md bg-emerald-50 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600">Cu CSD</p>
                      <p className="text-2xl font-bold text-emerald-700" data-testid={`agency-csd-${a.id}`}>{a.csd_total}</p>
                      {a.csd_cancelled > 0 && (
                        <p className="text-[10px] text-rose-500" title="Anulate după CSD — incluse conform regulii PPC">din care {a.csd_cancelled} anulate</p>
                      )}
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-400">
                        <th className="text-left font-medium pb-1">Produs</th>
                        <th className="text-right font-medium pb-1">Încărcate</th>
                        <th className="text-right font-medium pb-1">CSD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.categories.map((c) => (
                        <tr key={c.key} className="border-t border-slate-100">
                          <td className="py-1 text-slate-600">{c.label}{c.unit === "puncte" ? " (pct)" : ""}</td>
                          <td className="py-1 text-right font-medium text-slate-800">{a.uploaded[c.key]}</td>
                          <td className="py-1 text-right font-medium text-emerald-700">{a.csd[c.key]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
